import { DOWNLOAD_LINK_NAME_MAX_LENGTH, type DownloadLink } from '@pulsify/shared/dto'
import { slugify } from '@pulsify/shared/slug'
import { Archive, Copy, Download, Link2, QrCode } from 'lucide-react'
import { type FormEvent, useState } from 'react'
import { toast } from 'sonner'
import {
  useArchiveDownloadLink,
  useCreateDownloadLink,
  useDownloadLinks,
} from '~/entities/download-link/model/use-download-links'
import * as m from '~/paraglide/messages'
import { getLocale } from '~/paraglide/runtime.js'
import { copyToClipboard } from '~/shared/lib/share'
import { cn } from '~/shared/lib/utils'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/shared/ui/alert-dialog'
import { Badge } from '~/shared/ui/badge'
import { Button } from '~/shared/ui/button'
import { Card, CardContent } from '~/shared/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/shared/ui/dialog'
import { Input } from '~/shared/ui/input'
import { QrDownload } from '~/shared/ui/qr-download'
import { Skeleton } from '~/shared/ui/skeleton'
import { SparkBars } from '~/shared/ui/stacked-bars/spark-bars'

function CreateLinkForm() {
  const [name, setName] = useState('')
  const create = useCreateDownloadLink()

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return
    create.mutate(trimmed, { onSuccess: () => setName('') })
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-2 sm:flex-row">
      <Input
        value={name}
        onChange={(event) => setName(event.target.value)}
        maxLength={DOWNLOAD_LINK_NAME_MAX_LENGTH}
        placeholder={m.downloadLinksNamePlaceholder()}
        aria-label={m.downloadLinksNameLabel()}
        className="sm:max-w-md"
      />
      <Button type="submit" disabled={!name.trim() || create.isPending}>
        <Link2 className="mr-2 h-4 w-4" />
        {m.downloadLinksCreate()}
      </Button>
    </form>
  )
}

function QrDialog({ link }: { link: DownloadLink }) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-1.5"
          aria-label={m.downloadLinksShowQr()}
          title={m.downloadLinksShowQr()}
        >
          <QrCode className="h-3.5 w-3.5" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{link.name}</DialogTitle>
          <DialogDescription className="break-all font-mono text-xs">{link.url}</DialogDescription>
        </DialogHeader>
        <QrDownload
          value={link.url}
          title={m.downloadLinksQrAlt({ name: link.name })}
          fileName={`pulsify-${slugify(link.name) || link.code}`}
          downloadLabel={m.downloadLinksDownloadPng()}
        />
      </DialogContent>
    </Dialog>
  )
}

function ArchiveButton({ link }: { link: DownloadLink }) {
  const [open, setOpen] = useState(false)
  const archive = useArchiveDownloadLink()

  return (
    <>
      <Button variant="ghost" size="sm" disabled={archive.isPending} onClick={() => setOpen(true)}>
        <Archive className="mr-1.5 h-3.5 w-3.5" />
        {m.downloadLinksArchive()}
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{m.downloadLinksArchiveTitle({ name: link.name })}</AlertDialogTitle>
            <AlertDialogDescription>{m.downloadLinksArchiveBody()}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{m.cancel()}</AlertDialogCancel>
            <AlertDialogAction onClick={() => archive.mutate(link.id)}>
              {m.downloadLinksArchive()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function LinkRow({ link }: { link: DownloadLink }) {
  const locale = getLocale()
  const count = (value: number) => new Intl.NumberFormat(locale).format(value)
  const archived = link.archivedAt !== null

  const copy = async () => {
    if (await copyToClipboard(link.url)) toast.success(m.linkCopied())
    else toast.error(m.failedToCopyLink())
  }

  return (
    <tr className={cn('border-border border-b last:border-0', archived && 'text-muted-foreground')}>
      <td className="py-3 pr-4">
        <div className="flex items-center gap-2">
          <span className="font-medium">{link.name}</span>
          {archived ? (
            <Badge variant="outline" className="text-xs">
              {m.downloadLinksArchivedBadge()}
            </Badge>
          ) : null}
        </div>
      </td>
      <td className="py-3 pr-4">
        <div className="flex items-center gap-1">
          <span className="font-mono text-xs whitespace-nowrap">{link.url}</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-1.5"
            onClick={copy}
            aria-label={m.downloadLinksCopy()}
            title={m.downloadLinksCopy()}
          >
            <Copy className="h-3.5 w-3.5" />
          </Button>
          <QrDialog link={link} />
        </div>
      </td>
      <td className="py-3 pr-4 font-semibold tabular-nums">{count(link.clicks)}</td>
      <td className="py-3 pr-4 tabular-nums">{count(link.visitors)}</td>
      <td className="py-3 pr-4 tabular-nums whitespace-nowrap">
        {count(link.ios)} / {count(link.android)} / {count(link.other)}
      </td>
      <td className="py-3 pr-4">
        <div
          className="flex items-center gap-2"
          title={m.downloadLinksLast7DaysValues({ values: link.last7Days.join(' · ') })}
        >
          <SparkBars values={link.last7Days} width={56} height={20} />
          <span className="text-muted-foreground text-xs tabular-nums">
            {count(link.last7Days.reduce((sum, day) => sum + day, 0))}
          </span>
        </div>
      </td>
      <td className="py-3 pr-4 text-muted-foreground text-sm whitespace-nowrap">
        {new Date(link.createdAt).toLocaleDateString(locale, { dateStyle: 'medium' })}
      </td>
      <td className="py-3 pr-4 text-right">{archived ? null : <ArchiveButton link={link} />}</td>
    </tr>
  )
}

export function DownloadLinksPage() {
  const query = useDownloadLinks()
  const links = query.data ?? []

  return (
    <div className="px-4 sm:px-8 py-8 sm:py-16">
      <div className="space-y-8 mx-auto max-w-6xl">
        <header className="space-y-1.5">
          <h1 className="flex items-center gap-2.5 font-semibold text-foreground text-3xl tracking-tight">
            <Download className="w-7 h-7" aria-hidden />
            {m.downloadLinksTitle()}
          </h1>
          <p className="max-w-3xl text-muted-foreground text-sm">{m.downloadLinksHelp()}</p>
        </header>

        <CreateLinkForm />

        <Card>
          <CardContent className="p-0 overflow-x-auto">
            {query.isLoading ? (
              <div className="space-y-3 p-6">
                {['a', 'b', 'c', 'd'].map((placeholder) => (
                  <Skeleton key={placeholder} className="w-full h-10" />
                ))}
              </div>
            ) : links.length === 0 ? (
              <div className="flex flex-col items-center gap-2 p-12 text-center">
                <QrCode className="w-8 h-8 text-muted-foreground" aria-hidden />
                <p className="text-muted-foreground text-sm">{m.downloadLinksEmpty()}</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-border border-b text-muted-foreground text-xs text-left uppercase tracking-wide">
                    <th className="py-3 pr-4 pl-4 font-medium">{m.downloadLinksColumnName()}</th>
                    <th className="py-3 pr-4 font-medium">{m.downloadLinksColumnLink()}</th>
                    <th className="py-3 pr-4 font-medium">{m.downloadLinksColumnClicks()}</th>
                    <th className="py-3 pr-4 font-medium">{m.downloadLinksColumnPeople()}</th>
                    <th className="py-3 pr-4 font-medium">{m.downloadLinksColumnPlatforms()}</th>
                    <th className="py-3 pr-4 font-medium">{m.downloadLinksColumnLast7Days()}</th>
                    <th className="py-3 pr-4 font-medium">{m.downloadLinksColumnCreated()}</th>
                    <th className="py-3 pr-4 font-medium">
                      <span className="sr-only">{m.downloadLinksArchive()}</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="[&_td:first-child]:pl-4">
                  {links.map((link) => (
                    <LinkRow key={link.id} link={link} />
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
